import { useEffect, useRef, useState } from "react";
import type {
  Agent,
  EffectCategory,
  GameMap,
  Player,
  Round,
  RoundDetail,
  Utility,
} from "../api/types";
import { sampleAt, trail, type Tracks } from "../api/tracks";
import {
  AbilityLookup,
  EFFECT_STYLE,
  MAP_EFFECTS,
  effectsAt,
  sweepProgress,
  capturedPawn,
  utilityHeading,
  utilityMode,
  utilityPositionAt,
  utilityStateAt,
  type AbilityInfo,
} from "../lib/abilities";
import { image } from "../lib/images";
import { sideColor } from "../lib/view";
import { usePlayback } from "../player/store";
import { toScreen, toWorld, useCamera, type Viewport } from "../player/camera";
import { drawingsOf, useDrawings, type Shape as NoteShape } from "../player/drawings";
import { CameraTools } from "./CameraTools";
import { DrawTools } from "./DrawTools";

interface Props {
  map: GameMap;
  players: Player[];
  agents: Map<string, Agent>;
  lookup: AbilityLookup;
  round: Round;
  detail: RoundDetail;
  tracks: Tracks | undefined;
}

/** How long a death marker stays on the map. */
const DEATH_MARKER_MS = 5000;
/** Length of the motion trail behind each player. */
const TRAIL_MS = 3000;
/** Neutral colour for an ability whose owner could not be resolved. */
const UNOWNED = "#9aa4b5";

/**
 * The map view. It draws from a single animation frame loop that reads the
 * playback store directly, so moving the playhead never re-renders React.
 */
export function MapCanvas({ map, players, agents, lookup, round, detail, tracks }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Keep the latest props in a ref: the loop is started once and must not be
  // restarted on every render.
  const propsRef = useRef<Props>({ map, players, agents, lookup, round, detail, tracks });
  propsRef.current = { map, players, agents, lookup, round, detail, tracks };

  // Where the pointer is, in canvas pixels, and the stroke being drawn. Both
  // are refs, not state: the loop reads them every frame and React must not
  // re-render on mouse movement.
  const pointerRef = useRef<{ x: number; y: number } | null>(null);
  const draftRef = useRef<Draft | null>(null);

  // Where a written note is being typed. State, not a ref: the field is a real
  // element and has to render. It changes only when a note is placed.
  const [composing, setComposing] = useState<Composing | null>(null);
  const penColor = useDrawings((s) => s.color);

  // A drawing being dragged somewhere else, and the press that has not yet
  // decided whether it was a click on a player.
  const dragRef = useRef<Drag | null>(null);
  const pressRef = useRef<[number, number] | null>(null);
  /** Where the hand was last, while it is dragging the map itself. */
  const panRef = useRef<[number, number] | null>(null);
  /** Where the eraser last was while sweeping, or null when it is not down. */
  const erasingRef = useRef<[number, number] | null>(null);
  const [dragging, setDragging] = useState(false);

  /** Canvas pixels for a pointer event. */
  const canvasPoint = (event: React.PointerEvent<HTMLCanvasElement>): [number, number] | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    return [(event.clientX - rect.left) * dpr, (event.clientY - rect.top) * dpr];
  };

  /** The ground under a pointer event, in world centimetres. */
  const worldPoint = (event: React.PointerEvent<HTMLCanvasElement>): [number, number] | null => {
    const canvas = canvasRef.current;
    const point = canvasPoint(event);
    if (!canvas || !point) return null;
    return toWorld(propsRef.current.map, point[0], point[1], viewportOf(canvas), useCamera.getState());
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let frame = 0;
    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.round(wrap.clientWidth * dpr);
      canvas.height = Math.round(wrap.clientHeight * dpr);
      canvas.style.width = `${wrap.clientWidth}px`;
      canvas.style.height = `${wrap.clientHeight}px`;
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(wrap);

    const draw = () => {
      frame = requestAnimationFrame(draw);
      const p = propsRef.current;
      const { timeMs, focusPawn } = usePlayback.getState();
      const dpr = window.devicePixelRatio || 1;
      const w = canvas.width;
      const h = canvas.height;

      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, w, h);
      ctx.fillStyle = "#10131a";
      ctx.fillRect(0, 0, w, h);

      const camera = useCamera.getState();
      const viewport: Viewport = { width: w, height: h, map: Math.min(w, h) };
      const minimap = image(p.map.minimap);
      if (minimap) {
        // The image is transformed the same way the positions are, so the
        // ground and what stands on it stay together. It is a square centred
        // in a canvas that is not one.
        ctx.save();
        ctx.translate(w / 2 + camera.panX, h / 2 + camera.panY);
        ctx.rotate((camera.angle * Math.PI) / 180);
        ctx.scale(camera.zoom, camera.zoom);
        ctx.translate(-w / 2, -h / 2);
        ctx.globalAlpha = 0.55;
        ctx.drawImage(
          minimap,
          (w - viewport.map) / 2,
          (h - viewport.map) / 2,
          viewport.map,
          viewport.map,
        );
        ctx.globalAlpha = 1;
        ctx.restore();
      }

      const view: View = {
        ctx,
        dpr,
        at: (x, y) => toScreen(p.map, x, y, viewport, camera),
        // One centimetre of the world in pixels, from the map's own calibration
        // and how close the camera is. Ground scales; labels do not.
        cmToPx: Math.abs(p.map.xMultiplier) * viewport.map * camera.zoom,
        dimmed: (pawn) => focusPawn !== null && pawn !== focusPawn,
        focusPawn,
        timeMs,
        marks: [],
        ghosts: [],
      };

      drawUtility(view, p);
      drawSpike(view, p);
      drawDeaths(view, p);
      drawPlayers(view, p);
      drawGhosts(view);
      drawNotes(view, p, draftRef.current);
      drawHover(view, pointerRef.current);
    };

    // Registered by hand rather than as a prop: React listens passively for
    // wheel events, and a passive listener may not refuse the page scroll.
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      useCamera.getState().zoomAt(
        event.deltaY < 0 ? 1.15 : 1 / 1.15,
        [(event.clientX - rect.left) * dpr, (event.clientY - rect.top) * dpr],
        viewportOf(canvas),
      );
    };
    canvas.addEventListener("wheel", onWheel, { passive: false });

    frame = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      canvas.removeEventListener("wheel", onWheel);
    };
  }, []);

  const onPointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    const point = canvasPoint(event);
    if (!canvas || !point) return;
    const { tool } = useDrawings.getState();

    if (tool === "none") {
      // Only the owner moves notes; anybody else's drag pans the map.
      const editable = useDrawings.getState().replayId !== null;
      const hit = editable ? drawingAt(point) : null;
      const world = worldPoint(event);
      if (hit !== null && world) {
        const moving = useDrawings.getState().drawings.find((d) => d.id === hit);
        if (moving) {
          event.preventDefault();
          event.currentTarget.setPointerCapture(event.pointerId);
          dragRef.current = { id: hit, from: world, before: moving.points };
          setDragging(true);
          return;
        }
      }
      // Focus waits for the release, so a drag that started on nothing can
      // move the map instead of also changing who is being followed.
      event.currentTarget.setPointerCapture(event.pointerId);
      pressRef.current = point;
      panRef.current = point;
      return;
    }
    if (tool === "erase") {
      // The eraser is dragged over things rather than clicked on them, so the
      // pointer is held for the whole sweep and what it takes is one step.
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      erasingRef.current = point;
      useDrawings.getState().beginErase();
      eraseAlong(point, point);
      return;
    }
    const world = worldPoint(event);
    if (!world) return;
    if (tool === "text") {
      // A written note is placed, not dragged: the click opens a field on the
      // spot and what is typed there becomes the note. The default action has
      // to go, or the browser moves focus to the document on release and the
      // field this just opened is blurred away before a key reaches it.
      event.preventDefault();
      const dpr = window.devicePixelRatio || 1;
      setComposing({
        world,
        left: canvas.offsetLeft + point[0] / dpr,
        top: canvas.offsetTop + point[1] / dpr,
      });
      return;
    }
    // The pointer is captured so a stroke that leaves the canvas still ends.
    event.currentTarget.setPointerCapture(event.pointerId);
    draftRef.current = { shape: tool, points: [world, world] };
  };

  const onPointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const point = canvasPoint(event);
    pointerRef.current = point ? { x: point[0], y: point[1] } : null;

    const erasingFrom = erasingRef.current;
    if (erasingFrom) {
      if (point) {
        eraseAlong(erasingFrom, point);
        erasingRef.current = point;
      }
      return;
    }

    const panning = panRef.current;
    if (panning && point) {
      useCamera.getState().pan(point[0] - panning[0], point[1] - panning[1]);
      panRef.current = point;
      return;
    }

    const drag = dragRef.current;
    if (drag) {
      const world = worldPoint(event);
      if (!world) return;
      useDrawings.getState().nudge(drag.id, world[0] - drag.from[0], world[1] - drag.from[1]);
      drag.from = world;
      return;
    }

    const draft = draftRef.current;
    if (!draft) return;
    const world = worldPoint(event);
    if (!world) return;
    if (draft.shape === "pen") {
      // Thinned: a stroke needs its shape, not every pixel the mouse passed.
      const last = draft.points[draft.points.length - 1]!;
      if (Math.hypot(world[0] - last[0], world[1] - last[1]) > 40) draft.points.push(world);
      else draft.points[draft.points.length - 1] = world;
    } else {
      draft.points[1] = world;
    }
  };

  const onPointerUp = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (erasingRef.current) {
      erasingRef.current = null;
      useDrawings.getState().endErase(propsRef.current.round.number);
      return;
    }
    const drag = dragRef.current;
    if (drag) {
      dragRef.current = null;
      setDragging(false);
      useDrawings.getState().commitMove(drag.id, drag.before);
      return;
    }
    const press = pressRef.current;
    if (press) {
      pressRef.current = null;
      panRef.current = null;
      // Where the hand came off, not where it last moved: a plain click sends
      // no move event at all, and the old position would be from anywhere.
      const at = canvasPoint(event);
      if (!at || Math.hypot(at[0] - press[0], at[1] - press[1]) < 6) focusNearestPlayer(press);
      return;
    }
    const draft = draftRef.current;
    draftRef.current = null;
    if (!draft) return;
    const [first] = draft.points;
    const last = draft.points[draft.points.length - 1]!;
    // A click that never moved is not a drawing.
    if (draft.points.length < 3 && Math.hypot(last[0] - first![0], last[1] - first![1]) < 60) return;
    useDrawings.getState().add(propsRef.current.round.number, draft.shape, draft.points);
  };

  /** Clicking a player focuses them; clicking elsewhere clears the focus. */
  const focusNearestPlayer = ([x, y]: [number, number]) => {
    const canvas = canvasRef.current;
    const { tracks: t, players: ps, map: m } = propsRef.current;
    if (!canvas || !t) return;
    const { timeMs, focus } = usePlayback.getState();

    let closest: number | null = null;
    let best = 26 * (window.devicePixelRatio || 1);
    for (const player of ps) {
      const index = t.pawns.indexOf(player.pawn);
      if (index < 0) continue;
      const s = sampleAt(t, index, timeMs);
      if (!s.present) continue;
      const [px, py] = toScreen(m, s.x, s.y, viewportOf(canvas), useCamera.getState());
      const d = Math.hypot(px - x, py - y);
      if (d < best) {
        best = d;
        closest = player.pawn;
      }
    }
    focus(closest);
  };

  /** Removes the drawing nearest the click, when one is close enough. */
  /**
   * Takes everything the eraser passed over between two positions.
   *
   * The ground covered matters, not where the hand happened to be sampled: a
   * pointer reports a handful of times across a fast sweep, and testing only
   * those points lets the eraser step over a thin line without touching it.
   */
  const eraseAlong = (from: [number, number], to: [number, number]) => {
    const step = 5 * (window.devicePixelRatio || 1);
    const span = Math.hypot(to[0] - from[0], to[1] - from[1]);
    const samples = Math.max(1, Math.ceil(span / step));
    const taken = new Set<number>();
    for (let i = 0; i <= samples; i++) {
      const t = i / samples;
      const hit = drawingAt([from[0] + (to[0] - from[0]) * t, from[1] + (to[1] - from[1]) * t]);
      if (hit !== null) taken.add(hit);
    }
    for (const id of taken) useDrawings.getState().eraseOne(id);
  };

  /** The drawing under a point, when one is close enough to have been meant. */
  const drawingAt = ([x, y]: [number, number]): number | null => {
    const canvas = canvasRef.current;
    const { map: m, round } = propsRef.current;
    if (!canvas) return null;
    const { drawings } = useDrawings.getState();
    const reach = 12 * (window.devicePixelRatio || 1);

    let found: number | null = null;
    let best = reach;
    for (const drawing of drawingsOf(drawings, round.number)) {
      if (drawing.shape === "text") {
        // A written note is caught by its plate, which is what you see of it.
        const box = noteBoxes.get(drawing.id);
        if (box && x >= box[0] && x <= box[2] && y >= box[1] && y <= box[3]) {
          found = drawing.id;
          best = 0;
        }
        continue;
      }
      const screen = drawing.points.map(([wx, wy]) =>
        toScreen(m, wx, wy, viewportOf(canvas), useCamera.getState()),
      );
      for (let i = 0; i + 1 < screen.length; i++) {
        const d = distanceToSegment(x, y, screen[i]!, screen[i + 1]!);
        if (d < best) {
          best = d;
          found = drawing.id;
        }
      }
    }
    return found;
  };

  const tool = useDrawings((s) => s.tool);

  const commitNote = (text: string) => {
    const spot = composing;
    setComposing(null);
    if (!spot || !text.trim()) return;
    useDrawings.getState().add(propsRef.current.round.number, "text", [spot.world], text.trim());
  };

  return (
    <div ref={wrapRef} className="relative flex h-full w-full items-center justify-center">
      <canvas
        ref={canvasRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onMouseLeave={() => (pointerRef.current = null)}
        className={`rounded-lg ${
          dragging ? "cursor-grabbing" : tool === "none" ? "cursor-pointer" : "cursor-crosshair"
        }`}
      />
      <div className="pointer-events-none absolute left-1/2 top-2 -translate-x-1/2">
        <DrawTools round={round.number} />
      </div>
      {/* Bottom right, clear of the drawing palette: at a narrow width the two
          ran into each other along the top edge. */}
      <div className="pointer-events-none absolute bottom-2 right-2">
        <CameraTools />
      </div>
      {composing && (
        <input
          autoFocus
          maxLength={120}
          placeholder="Note…"
          onBlur={(e) => commitNote(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitNote(e.currentTarget.value);
            if (e.key === "Escape") setComposing(null);
            e.stopPropagation();
          }}
          className="absolute w-44 -translate-x-1/2 -translate-y-full rounded border bg-black/85 px-2 py-1 text-xs outline-none"
          style={{
            left: composing.left,
            top: composing.top - 8,
            borderColor: penColor,
            color: penColor,
          }}
        />
      )}
    </div>
  );
}

/** The canvas, and the square the map occupies inside it. */
function viewportOf(canvas: HTMLCanvasElement): Viewport {
  return {
    width: canvas.width,
    height: canvas.height,
    map: Math.min(canvas.width, canvas.height),
  };
}

/** A drawing being dragged: which one, where the hand is now, and where it was
 *  before, to put it back if the move cannot be saved. */
interface Drag {
  id: number;
  from: [number, number];
  before: Array<[number, number]>;
}

/** A written note being typed: where it belongs in the world, and where to put
 *  the field on screen, in CSS pixels within the wrapper. */
interface Composing {
  world: [number, number];
  left: number;
  top: number;
}

/** A stroke in progress, in world centimetres. */
interface Draft {
  shape: NoteShape;
  points: Array<[number, number]>;
}

/** Distance from a point to a segment, all in canvas pixels. */
function distanceToSegment(
  x: number,
  y: number,
  a: [number, number],
  b: [number, number],
): number {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const length = dx * dx + dy * dy;
  const t = length > 0 ? Math.max(0, Math.min(1, ((x - a[0]) * dx + (y - a[1]) * dy) / length)) : 0;
  return Math.hypot(x - (a[0] + dx * t), y - (a[1] + dy * t));
}

/**
 * The notes drawn on the map, over everything else because that is the point of
 * them. A stroke in progress is drawn the same way as a finished one, so what
 * you are dragging out is what you get.
 */
function drawNotes(v: View, p: Props, draft: Draft | null) {
  const { ctx, dpr } = v;
  const { drawings, color: penColor } = useDrawings.getState();
  const all: Array<{ id: number; shape: NoteShape; color: string; points: Array<[number, number]>; text?: string }> =
    drawingsOf(drawings, p.round.number).map((d) => ({
      id: d.id,
      shape: d.shape,
      color: d.color,
      points: d.points,
      text: d.text,
    }));
  if (draft) {
    all.push({ id: 0, shape: draft.shape, color: penColor, points: draft.points });
  }

  noteBoxes.clear();
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  for (const note of all) {
    const screen = note.points.map(([wx, wy]) => v.at(wx, wy));
    if (screen.length === 0) continue;

    if (note.shape === "text") {
      drawNoteLabel(v, note.id, screen[0]!, note.text ?? "", note.color);
      continue;
    }
    if (screen.length < 2) continue;

    ctx.globalAlpha = 0.95;
    ctx.strokeStyle = note.color;
    ctx.lineWidth = 2.5 * dpr;
    ctx.beginPath();
    ctx.moveTo(screen[0]![0], screen[0]![1]);
    for (let i = 1; i < screen.length; i++) ctx.lineTo(screen[i]![0], screen[i]![1]);
    ctx.stroke();

    if (note.shape === "arrow") {
      const from = screen[screen.length - 2]!;
      const to = screen[screen.length - 1]!;
      const angle = Math.atan2(to[1] - from[1], to[0] - from[0]);
      const head = 11 * dpr;
      ctx.beginPath();
      ctx.moveTo(to[0], to[1]);
      ctx.lineTo(to[0] - head * Math.cos(angle - 0.4), to[1] - head * Math.sin(angle - 0.4));
      ctx.lineTo(to[0] - head * Math.cos(angle + 0.4), to[1] - head * Math.sin(angle + 0.4));
      ctx.closePath();
      ctx.fillStyle = note.color;
      ctx.fill();
    }
  }
  ctx.globalAlpha = 1;
}

/**
 * Where each written note ended up on screen, so the eraser can find one. It is
 * rewritten every frame by the only map on screen, which is why it can live
 * out here rather than being threaded through.
 */
const noteBoxes = new Map<number, [number, number, number, number]>();

/** A written note: a dot on the spot it is about, and the words above it. */
function drawNoteLabel(
  v: View,
  id: number,
  [x, y]: [number, number],
  text: string,
  color: string,
) {
  const { ctx, dpr } = v;
  ctx.font = `600 ${12 * dpr}px system-ui, sans-serif`;
  ctx.textBaseline = "middle";
  ctx.textAlign = "left";

  const padding = 6 * dpr;
  const height = 20 * dpr;
  const width = ctx.measureText(text).width + padding * 2;
  const left = x - width / 2;
  const top = y - height - 10 * dpr;

  ctx.globalAlpha = 1;
  ctx.fillStyle = "rgba(8, 11, 18, 0.88)";
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.2 * dpr;
  ctx.beginPath();
  ctx.roundRect(left, top, width, height, 4 * dpr);
  ctx.fill();
  ctx.stroke();

  // A stem down to the exact spot, so the words are not floating free of it.
  ctx.beginPath();
  ctx.moveTo(x, top + height);
  ctx.lineTo(x, y - 3 * dpr);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(x, y, 2.5 * dpr, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();

  ctx.fillStyle = color;
  ctx.fillText(text, left + padding, top + height / 2);
  ctx.textBaseline = "alphabetic";

  if (id !== 0) noteBoxes.set(id, [left, top, left + width, y + 4 * dpr]);
}

/** The name of the ability under the pointer, and nothing else. */
function drawHover(v: View, at: { x: number; y: number } | null) {
  if (!at) return;
  let found: Mark | null = null;
  let best = Infinity;
  for (const mark of v.marks) {
    const d = Math.hypot(mark.x - at.x, mark.y - at.y);
    if (d <= mark.radius + 6 * v.dpr && d < best) {
      best = d;
      found = mark;
    }
  }
  if (!found) return;

  const { ctx, dpr } = v;
  ctx.font = `600 ${11 * dpr}px system-ui, sans-serif`;
  const padding = 7 * dpr;
  const boxW = ctx.measureText(found.info.name).width + padding * 2;
  const boxH = padding * 2 + 11 * dpr;
  // Kept inside the canvas so a card near an edge stays readable.
  const x = Math.min(Math.max(found.x + 14 * dpr, 4 * dpr), ctx.canvas.width - boxW - 4 * dpr);
  const y = Math.min(Math.max(found.y - boxH / 2, 4 * dpr), ctx.canvas.height - boxH - 4 * dpr);

  ctx.globalAlpha = 1;
  ctx.fillStyle = "rgba(8, 11, 18, 0.94)";
  ctx.strokeStyle = "rgba(255, 255, 255, 0.18)";
  ctx.lineWidth = dpr;
  ctx.beginPath();
  ctx.roundRect(x, y, boxW, boxH, 5 * dpr);
  ctx.fill();
  ctx.stroke();

  ctx.textBaseline = "top";
  ctx.fillStyle = "#fff";
  ctx.fillText(found.info.name, x + padding, y + padding);
  ctx.textBaseline = "alphabetic";
}

interface View {
  ctx: CanvasRenderingContext2D;
  dpr: number;
  at: (x: number, y: number) => [number, number];
  cmToPx: number;
  dimmed: (pawn: number | undefined) => boolean;
  focusPawn: number | null;
  timeMs: number;
  /** Where each ability ended up on screen, so the pointer can find it. */
  marks: Mark[];
  /** Spots a player was taken from, painted last so a live marker standing on
   *  the same ground does not hide them. */
  ghosts: Array<{ x: number; y: number; color: string; alpha: number }>;
}

interface Mark {
  x: number;
  y: number;
  radius: number;
  info: AbilityInfo;
}

/** Draws an ability's icon and remembers where it landed. */
function plate(s: Shape, x: number, y: number, radius: number) {
  drawIcon(s.v, x, y, radius, s.info.icon, s.color, s.alpha, s.state);
  s.v.marks.push({ x, y, radius, info: s.info });
}

/**
 * Abilities, drawn the way they exist in the world.
 *
 * A disc is the right picture for a smoke and the wrong one for most of the
 * rest, so each kind gets its own drawing: a wall between the anchors the game
 * replicated, a camera's field of view, a trap's detection ring around the
 * smaller zone it acts on, two parallel walls for a lane, a cross for a mesh,
 * the blocks of a segmented barrier, the front of an ultimate advancing across
 * the map. Anything that can be switched off is drawn hollow while it is off,
 * and anything covert is drawn dashed.
 */
function drawUtility(v: View, p: Props) {
  const { ctx, timeMs } = v;
  // Several abilities can be running on the same player at once, so each takes
  // the next place around them instead of stacking on the last.
  const onPlayer = new Map<number, number>();
  for (const u of p.detail.utility) {
    if (u.spawnedMs > timeMs) continue;
    if (u.despawnedMs !== undefined && u.despawnedMs < timeMs) continue;

    const info = p.lookup.describe(u);
    // A teleport's actor outlives the teleport by the whole round.
    if (info.durationMs > 0 && timeMs > u.spawnedMs + info.durationMs) continue;
    const owner = p.players.find((pl) => pl.pawn === u.owner);
    const color = owner ? sideColor(owner, p.round) : UNOWNED;
    const alpha = v.dimmed(u.owner) ? 0.18 : 1;
    const state = utilityStateAt(u, timeMs);

    const position = utilityPositionAt(u, timeMs);
    const heading = utilityHeading(u, timeMs);
    const mode = utilityMode(u, info.modes, p.detail.effects);
    const shape: Shape = { v, p, u, info, color, alpha, state, position, heading, mode };

    // An ability that is not an object on the map is drawn where it actually
    // happens: on the player who cast it. A gun is not drawn at all — it is
    // already in the loadout.
    if (info.placement === "weapon") continue;
    if (info.placement !== "world") {
      const pawn = u.owner ?? 0;
      const seat = onPlayer.get(pawn) ?? 0;
      onPlayer.set(pawn, seat + 1);
      drawOnCaster(shape, seat);
      continue;
    }

    switch (info.kind) {
      case "wall":
      case "line":
        drawSpan(shape, info.kind === "line" ? 2.5 : 6);
        break;
      case "barrier":
        drawBarrier(shape);
        break;
      case "lane":
        drawLane(shape);
        break;
      case "cross":
        drawCross(shape);
        break;
      case "cone":
        drawCone(shape);
        break;
      case "sweep":
        drawSweep(shape);
        break;
      case "capture":
        drawCapture(shape);
        break;
      case "arena":
        drawArena(shape);
        break;
      case "rect":
        drawRect(shape, info.lengthCm, info.widthCm);
        break;
      case "cylinder":
        drawCylinder(shape);
        break;
      case "zone":
        drawZone(shape);
        break;
      default:
        drawObject(shape);
    }
  }
  ctx.globalAlpha = 1;
}

/** Everything one ability's drawing needs, gathered once. */
interface Shape {
  v: View;
  p: Props;
  u: Utility;
  info: AbilityInfo;
  color: string;
  alpha: number;
  state: { active: boolean; hidden: boolean };
  position: [number, number] | null;
  heading: number | null;
  /** Which outcome an ability with two of them actually produced. */
  mode: string | null;
}

/**
 * The two world-space ends of anything laid out as a span.
 *
 * The replicated anchors are used when the game gave them. Failing that, a wall
 * that was laid down along a path runs the length of that path: Neon's lane and
 * Harbor's water rise behind a projectile, so centring them on where the
 * projectile stopped pushes the whole wall past where it really is. Only when
 * there is neither is the curated length laid along the ability's facing.
 */
function endpoints(s: Shape): [number, number, number, number] | null {
  const { u, info } = s;
  if (u.wall) return [u.wall.startX, u.wall.startY, u.wall.endX, u.wall.endY];

  const path = u.path;
  if (path && path.length > 1) {
    const first = path[0]!;
    const last = path[path.length - 1]!;
    if (Math.hypot(last.x - first.x, last.y - first.y) > 100) {
      return [first.x, first.y, last.x, last.y];
    }
  }

  if (!s.position || s.heading === null) return null;
  const half = (info.lengthCm || 700) / 2;
  const [cx, cy] = s.position;
  const dx = Math.cos(s.heading) * half;
  const dy = Math.sin(s.heading) * half;
  return [cx - dx, cy - dy, cx + dx, cy + dy];
}

/** A solid span: a wall, or the thin line of a strung trap. */
function drawSpan(s: Shape, weight: number) {
  const ends = endpoints(s);
  if (!ends) {
    drawObject(s);
    return;
  }
  const { ctx, dpr } = s.v;
  const [ax, ay, bx, by] = ends;
  const [sx, sy] = s.v.at(ax, ay);
  const [ex, ey] = s.v.at(bx, by);

  ctx.globalAlpha = s.alpha * (s.state.active ? 0.75 : 0.3);
  ctx.strokeStyle = s.color;
  ctx.lineCap = "round";
  ctx.lineWidth = weight * dpr;
  // Covert or switched off reads as a broken line; live reads as solid.
  ctx.setLineDash(s.state.hidden || !s.state.active ? [5 * dpr, 5 * dpr] : []);
  ctx.beginPath();
  ctx.moveTo(sx, sy);
  ctx.lineTo(ex, ey);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.lineWidth = 1;
  plate(s, (sx + ex) / 2, (sy + ey) / 2, 9 * dpr);
}

/**
 * A barrier of blocks that break one at a time, drawn block by block so the
 * gaps in a partly broken wall are visible.
 */
function drawBarrier(s: Shape) {
  const ends = endpoints(s);
  if (!ends) {
    drawObject(s);
    return;
  }
  const { ctx, dpr } = s.v;
  const [ax, ay, bx, by] = ends;
  const count = Math.max(1, s.info.segments || 1);
  const gap = 0.12; // share of a block left empty between blocks

  ctx.globalAlpha = s.alpha * (s.state.active ? 0.8 : 0.3);
  ctx.strokeStyle = s.color;
  ctx.lineCap = "butt";
  ctx.lineWidth = 7 * dpr;
  for (let i = 0; i < count; i++) {
    const from = (i + gap / 2) / count;
    const to = (i + 1 - gap / 2) / count;
    const [x1, y1] = s.v.at(ax + (bx - ax) * from, ay + (by - ay) * from);
    const [x2, y2] = s.v.at(ax + (bx - ax) * to, ay + (by - ay) * to);
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
  }
  ctx.lineWidth = 1;
  const [mx, my] = s.v.at((ax + bx) / 2, (ay + by) / 2);
  plate(s, mx, my, 9 * dpr);
}

/** Two parallel walls, the way Neon's fast lane actually comes out. */
function drawLane(s: Shape) {
  const ends = endpoints(s);
  if (!ends) {
    drawObject(s);
    return;
  }
  const { ctx, dpr } = s.v;
  const [ax, ay, bx, by] = ends;
  const length = Math.hypot(bx - ax, by - ay) || 1;
  const half = (s.info.widthCm || 400) / 2;
  // Perpendicular to the lane, in world units.
  const nx = (-(by - ay) / length) * half;
  const ny = ((bx - ax) / length) * half;

  ctx.globalAlpha = s.alpha * (s.state.active ? 0.75 : 0.3);
  ctx.strokeStyle = s.color;
  ctx.lineCap = "round";
  ctx.lineWidth = 5 * dpr;
  for (const side of [1, -1]) {
    const [x1, y1] = s.v.at(ax + nx * side, ay + ny * side);
    const [x2, y2] = s.v.at(bx + nx * side, by + ny * side);
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
  }
  ctx.lineWidth = 1;
  const [mx, my] = s.v.at((ax + bx) / 2, (ay + by) / 2);
  plate(s, mx, my, 9 * dpr);
}

/** Barriers radiating from one point: Deadlock's mesh is a cross, not a disc. */
function drawCross(s: Shape) {
  if (!s.position) return;
  const { ctx, dpr } = s.v;
  const [cx, cy] = s.position;
  const arm = (s.info.lengthCm || 1400) / 2;
  const base = s.heading ?? 0;
  const arms = Math.max(2, s.info.segments || 4);

  ctx.globalAlpha = s.alpha * (s.state.active ? 0.75 : 0.3);
  ctx.strokeStyle = s.color;
  ctx.lineCap = "round";
  ctx.lineWidth = 5 * dpr;
  for (let i = 0; i < arms; i++) {
    const angle = base + (i * 2 * Math.PI) / arms;
    const [x1, y1] = s.v.at(cx, cy);
    const [x2, y2] = s.v.at(cx + Math.cos(angle) * arm, cy + Math.sin(angle) * arm);
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
  }
  ctx.lineWidth = 1;
  const [x, y] = s.v.at(cx, cy);
  plate(s, x, y, 9 * dpr);
}

/** A field of view: what a camera or a turret is looking at. */
function drawCone(s: Shape) {
  if (!s.position) return;
  const { ctx, dpr } = s.v;
  const [cx, cy] = s.position;
  const [x, y] = s.v.at(cx, cy);

  if (s.heading !== null && s.state.active) {
    const reach = (s.info.rangeCm || 2500) * s.v.cmToPx;
    const spread = ((s.info.coneDeg || 80) * Math.PI) / 360;
    // Screen space does not share the world's axes, so the heading is turned
    // into one by projecting a step taken along it.
    const [px, py] = s.v.at(cx + Math.cos(s.heading) * 100, cy + Math.sin(s.heading) * 100);
    const screenAngle = Math.atan2(py - y, px - x);

    const wedge = ctx.createRadialGradient(x, y, 0, x, y, reach);
    wedge.addColorStop(0, s.color);
    wedge.addColorStop(0.45, s.color);
    wedge.addColorStop(1, "transparent");
    ctx.globalAlpha = s.alpha * 0.24;
    ctx.fillStyle = wedge;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.arc(x, y, reach, screenAngle - spread, screenAngle + spread);
    ctx.closePath();
    ctx.fill();
    // The two edges of the field, so where it stops is unambiguous.
    ctx.globalAlpha = s.alpha * 0.45;
    ctx.strokeStyle = s.color;
    ctx.lineWidth = 1.2 * dpr;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.arc(x, y, reach, screenAngle - spread, screenAngle + spread);
    ctx.closePath();
    ctx.stroke();
  }
  plate(s, x, y, 9 * dpr);
}

/**
 * An ultimate that travels: Breach's quake, Fade's wave. The ground already
 * crossed is shaded and the front is a bar across the direction of travel, so
 * what has been hit reads differently from what is about to be.
 */
function drawSweep(s: Shape) {
  if (!s.position) return;
  const { ctx, dpr } = s.v;
  const progress = sweepProgress(s.u, s.v.timeMs, s.info.advances);
  const heading = s.heading ?? 0;
  const reach = s.info.lengthCm || 3000;
  const half = (s.info.widthCm || 600) / 2;

  // Start from where the ability came into the world, not where it is now.
  const path = s.u.path;
  const origin: [number, number] = path && path.length > 0 ? [path[0]!.x, path[0]!.y] : s.position;
  const front: [number, number] = [
    origin[0] + Math.cos(heading) * reach * progress,
    origin[1] + Math.sin(heading) * reach * progress,
  ];
  const nx = -Math.sin(heading) * half;
  const ny = Math.cos(heading) * half;

  const corners: Array<[number, number]> = [
    [origin[0] + nx, origin[1] + ny],
    [front[0] + nx, front[1] + ny],
    [front[0] - nx, front[1] - ny],
    [origin[0] - nx, origin[1] - ny],
  ];
  ctx.globalAlpha = s.alpha * 0.2;
  ctx.fillStyle = s.color;
  ctx.beginPath();
  corners.forEach(([wx, wy], i) => {
    const [px, py] = s.v.at(wx, wy);
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  });
  ctx.closePath();
  ctx.fill();

  // The front itself, while it is still moving.
  if (progress < 1) {
    const [x1, y1] = s.v.at(front[0] + nx, front[1] + ny);
    const [x2, y2] = s.v.at(front[0] - nx, front[1] - ny);
    ctx.globalAlpha = s.alpha * 0.9;
    ctx.strokeStyle = s.color;
    ctx.lineCap = "round";
    ctx.lineWidth = 4 * dpr;
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
    ctx.lineWidth = 1;
  }
  const [fx, fy] = s.v.at(front[0], front[1]);
  plate(s, fx, fy, 9 * dpr);
}

/**
 * An ability that acts on the player who cast it, or on the whole map: drawn
 * beside that player rather than at some arbitrary spot in the world, because
 * that is where it is happening. A map-wide ability also sends a ring out over
 * everything for as long as it takes to land.
 */
function drawOnCaster(s: Shape, seat: number) {
  const { ctx, dpr, timeMs } = s.v;
  const pawn = s.u.owner;
  let spot = s.position;
  if (pawn !== undefined) {
    const live = pawnPosition(s, pawn);
    if (live) spot = live;
  }
  if (!spot) return;
  const [x, y] = s.v.at(spot[0], spot[1]);

  if (s.info.placement === "global") {
    const age = timeMs - s.u.spawnedMs;
    const PULSE_MS = 1500;
    if (age >= 0 && age < PULSE_MS) {
      const t = age / PULSE_MS;
      ctx.globalAlpha = s.alpha * 0.5 * (1 - t);
      ctx.strokeStyle = s.color;
      ctx.lineWidth = 2 * dpr;
      ctx.beginPath();
      ctx.arc(x, y, t * s.v.ctx.canvas.width, 0, Math.PI * 2);
      ctx.stroke();
    }
  }

  // Seated around the player marker rather than on top of it.
  const angle = -Math.PI / 4 + seat * (Math.PI / 5);
  const px = x + Math.cos(angle) * 20 * dpr;
  const py = y + Math.sin(angle) * 20 * dpr;
  plate(s, px, py, 8 * dpr);
}

/**
 * A rectangle laid out in front of where it was cast: Breach's charges go off
 * through a wall over a patch of ground, not in a circle around themselves.
 */
function drawRect(s: Shape, lengthCm: number, widthCm: number) {
  if (!s.position) return;
  const { ctx, dpr } = s.v;
  const heading = s.heading ?? 0;
  const reach = lengthCm || 700;
  const half = (widthCm || 500) / 2;
  const [ox, oy] = s.position;
  const nx = -Math.sin(heading) * half;
  const ny = Math.cos(heading) * half;
  const fx = ox + Math.cos(heading) * reach;
  const fy = oy + Math.sin(heading) * reach;

  const corners: Array<[number, number]> = [
    [ox + nx, oy + ny],
    [fx + nx, fy + ny],
    [fx - nx, fy - ny],
    [ox - nx, oy - ny],
  ];
  ctx.beginPath();
  corners.forEach(([wx, wy], i) => {
    const [px, py] = s.v.at(wx, wy);
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  });
  ctx.closePath();
  ctx.globalAlpha = s.alpha * (s.state.active ? 0.22 : 0.1);
  ctx.fillStyle = s.color;
  ctx.fill();
  ctx.globalAlpha = s.alpha * 0.65;
  ctx.strokeStyle = s.color;
  ctx.lineWidth = 1.5 * dpr;
  ctx.setLineDash(s.info.approximate ? [4 * dpr, 3 * dpr] : []);
  ctx.stroke();
  ctx.setLineDash([]);

  const [mx, my] = s.v.at((ox + fx) / 2, (oy + fy) / 2);
  plate(s, mx, my, 9 * dpr);
}

/**
 * Something that flies out and takes hold of a player. The pulse crosses its
 * whole path the moment it is fired, so the path is drawn in full from the
 * start; what moves afterwards is the victim. Which victim is not something the
 * ability tells us by moving — it is the player the replay applied the effect
 * to, so the marker rides that player rather than guessing a speed along the
 * wire.
 */
function drawCapture(s: Shape) {
  const { ctx, dpr } = s.v;
  drawTravelled(s, 0.45, true);

  const caught = capturedPawn(s.u, s.p.detail.effects);
  const victim = caught !== null ? pawnPosition(s, caught) : null;
  const anchor = victim ?? s.position;
  if (!anchor) return;

  const path = s.u.path;
  if (path && path.length > 0) {
    const [ox, oy] = s.v.at(path[0]!.x, path[0]!.y);
    const [cx, cy] = s.v.at(anchor[0], anchor[1]);
    ctx.globalAlpha = s.alpha * 0.5;
    ctx.strokeStyle = s.color;
    ctx.setLineDash([3 * dpr, 4 * dpr]);
    ctx.lineWidth = 1.5 * dpr;
    ctx.beginPath();
    ctx.moveTo(ox, oy);
    ctx.lineTo(cx, cy);
    ctx.stroke();
    ctx.setLineDash([]);
  }
  const [x, y] = s.v.at(anchor[0], anchor[1]);
  plate(s, x, y, 10 * dpr);
}

/**
 * An ability that takes two players out of the world and settles it between
 * them somewhere else. What is worth seeing on the map is the ground it reached
 * into — a column thrown forward, not a circle around the caster — and where
 * the two of them were standing when it took them, because that is the last
 * thing that happened here.
 */
function drawArena(s: Shape) {
  if (!s.position) return;
  drawRect(s, s.info.lengthCm, s.info.widthCm);

  // Where each of the two stood at the moment they were taken.
  const caught = capturedPawn(s.u, s.p.detail.effects);
  for (const pawn of [s.u.owner, caught ?? undefined]) {
    if (pawn === undefined) continue;
    const spot = pawnPosition(s, pawn, s.u.spawnedMs);
    if (spot) drawGhost(s, spot);
  }
}

/** Notes the spot a player was taken from. It is painted at the end of the
 *  frame, because the player may still be standing on that very ground. */
function drawGhost(s: Shape, spot: [number, number]) {
  const [x, y] = s.v.at(spot[0], spot[1]);
  s.v.ghosts.push({ x, y, color: s.color, alpha: s.alpha });
}

/** The outlines left where players were taken out of the world. */
function drawGhosts(v: View) {
  const { ctx, dpr } = v;
  for (const ghost of v.ghosts) {
    const r = 14 * dpr;
    ctx.globalAlpha = ghost.alpha * 0.9;
    ctx.strokeStyle = ghost.color;
    ctx.lineWidth = 1.5 * dpr;
    ctx.setLineDash([2 * dpr, 3 * dpr]);
    ctx.beginPath();
    ctx.arc(ghost.x, ghost.y, r, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);
    // A cross through it, so an empty spot does not read as a live player.
    ctx.beginPath();
    ctx.moveTo(ghost.x - r * 0.55, ghost.y - r * 0.55);
    ctx.lineTo(ghost.x + r * 0.55, ghost.y + r * 0.55);
    ctx.moveTo(ghost.x + r * 0.55, ghost.y - r * 0.55);
    ctx.lineTo(ghost.x - r * 0.55, ghost.y + r * 0.55);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
}

/** Where a pawn was at an instant, from the playback grid. */
function pawnPosition(s: Shape, pawn: number, timeMs?: number): [number, number] | null {
  const tracks = s.p.tracks;
  if (!tracks) return null;
  const index = tracks.pawns.indexOf(pawn);
  if (index < 0) return null;
  const sample = sampleAt(tracks, index, timeMs ?? s.v.timeMs);
  return sample.present ? [sample.x, sample.y] : null;
}

/**
 * A cylinder laid along the surface the ability stuck to. Breach's charges go
 * off through whatever they are attached to: flat against a wall the cylinder
 * lies down and covers a strip of ground, stuck to the floor it stands up and
 * covers a disc. The spawn tilt is what tells the two apart.
 */
function drawCylinder(s: Shape) {
  const tilt = Math.abs(((s.u.pitch + 180) % 360) - 180);
  if (tilt > 45) {
    drawZone(s);
    return;
  }
  drawRect(s, s.info.lengthCm, s.info.radiusCm * 2);
}

/** A disc of the ability's real radius, with the wider ring a trap watches. */
function drawZone(s: Shape) {
  if (!s.position) return;
  const { ctx, dpr } = s.v;
  const [x, y] = s.v.at(s.position[0], s.position[1]);
  const radius = s.info.radiusCm * s.v.cmToPx;
  if (radius <= 0) {
    drawObject(s);
    return;
  }

  drawDetection(s, x, y);

  // A zone reads as an area first: a soft fill, a crisp edge, a small icon. A
  // zone that has not been switched on gets no fill at all.
  if (s.state.active) {
    ctx.globalAlpha = s.alpha * 0.22;
    ctx.fillStyle = s.color;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = s.alpha * (s.state.active ? 0.7 : 0.35);
  ctx.strokeStyle = s.color;
  ctx.lineWidth = 1.5 * dpr;
  ctx.setLineDash(s.info.approximate || !s.state.active ? [4 * dpr, 3 * dpr] : []);
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.stroke();
  ctx.setLineDash([]);
  const size = Math.min(11 * dpr, Math.max(7 * dpr, radius * 0.45));
  plate(s, x, y, size);
  drawTag(s, x, y + size + 9 * dpr);
}

/** A small object: what it flew through, then its icon. */
function drawObject(s: Shape) {
  if (!s.position) return;
  const { dpr } = s.v;
  drawTravelled(s, 0.35);
  const [x, y] = s.v.at(s.position[0], s.position[1]);
  drawDetection(s, x, y);
  const age = s.v.timeMs - s.u.spawnedMs;
  const pop = age < 350 ? 1 + (1 - age / 350) * 0.6 : 1;
  plate(s, x, y, 10 * dpr * pop);
  drawTag(s, x, y + 19 * dpr);
}

/**
 * A word under an ability when the icon alone would mislead: which of two
 * outcomes it produced, or that it has dropped to the ground and can be picked
 * back up.
 */
function drawTag(s: Shape, x: number, y: number) {
  if (s.mode) {
    const style = EFFECT_STYLE[s.mode as EffectCategory];
    drawLabel(s, x, y, style?.label ?? s.mode, style?.color ?? s.color);
    return;
  }
  if (s.info.dormant && !s.state.active) drawLabel(s, x, y, "au sol", s.color);
}

/** A small caption on a plate, centred on x. */
function drawLabel(s: Shape, x: number, y: number, label: string, color: string) {
  const { ctx, dpr } = s.v;
  ctx.globalAlpha = s.alpha * 0.85;
  ctx.font = `${9 * dpr}px system-ui, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const width = ctx.measureText(label).width + 6 * dpr;
  ctx.fillStyle = "rgba(8, 11, 18, 0.85)";
  ctx.beginPath();
  ctx.roundRect(x - width / 2, y - 6 * dpr, width, 12 * dpr, 3 * dpr);
  ctx.fill();
  ctx.fillStyle = color;
  ctx.fillText(label, x, y);
  ctx.textAlign = "start";
  ctx.textBaseline = "alphabetic";
}

/** The ring a trap watches, always wider than the zone it acts on. */
function drawDetection(s: Shape, x: number, y: number) {
  if (s.info.detectCm <= 0 || !s.state.active) return;
  const { ctx, dpr } = s.v;
  ctx.globalAlpha = s.alpha * 0.3;
  ctx.strokeStyle = s.color;
  ctx.lineWidth = 1 * dpr;
  ctx.setLineDash([2 * dpr, 5 * dpr]);
  ctx.beginPath();
  ctx.arc(x, y, s.info.detectCm * s.v.cmToPx, 0, Math.PI * 2);
  ctx.stroke();
  ctx.setLineDash([]);
}

/** The ground a moving ability has covered, up to now or in full. */
function drawTravelled(s: Shape, strength: number, whole = false) {
  const path = s.u.path;
  if (!path || path.length < 2) return;
  const { ctx, dpr } = s.v;
  ctx.globalAlpha = s.alpha * strength;
  ctx.strokeStyle = s.color;
  ctx.lineWidth = 1.5 * dpr;
  ctx.beginPath();
  let started = false;
  for (const point of path) {
    if (!whole && point.t > s.v.timeMs) break;
    const [px, py] = s.v.at(point.x, point.y);
    if (!started) {
      ctx.moveTo(px, py);
      started = true;
    } else {
      ctx.lineTo(px, py);
    }
  }
  if (started) ctx.stroke();
}

/**
 * An ability icon on a rounded plate bordered in its owner's colour.
 *
 * The plate is square where a player marker is round: that is what separates
 * an ability from a person at a glance, and it matches how the game itself
 * shows abilities. An ability that is switched off, dropped or waiting to be
 * picked up is drawn faint, and a covert one gets a broken border.
 */
function drawIcon(
  v: View,
  x: number,
  y: number,
  radius: number,
  icon: string | undefined,
  color: string,
  alpha: number,
  state: { active: boolean; hidden: boolean } = { active: true, hidden: false },
) {
  const { ctx } = v;
  const art = image(icon);
  const size = radius * 2;
  const corner = radius * 0.35;
  const shown = alpha * (state.active ? 1 : 0.45);

  ctx.globalAlpha = shown;
  ctx.beginPath();
  ctx.roundRect(x - radius, y - radius, size, size, corner);
  ctx.closePath();
  ctx.fillStyle = "rgba(8, 11, 18, 0.88)";
  ctx.fill();

  if (art) {
    ctx.save();
    ctx.clip();
    // The reference icons are white silhouettes; a slight inset keeps them
    // clear of the border.
    const inset = radius * 0.18;
    ctx.drawImage(art, x - radius + inset, y - radius + inset, size - inset * 2, size - inset * 2);
    ctx.restore();
  } else {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(x, y, radius * 0.45, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(1, v.dpr * 1.5);
  ctx.setLineDash(state.hidden ? [3 * v.dpr, 3 * v.dpr] : []);
  ctx.beginPath();
  ctx.roundRect(x - radius, y - radius, size, size, corner);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.globalAlpha = 1;
}

function drawSpike(v: View, p: Props) {
  const { ctx, dpr, timeMs } = v;
  const plant = p.detail.events.find((e) => e.kind === "plant");
  if (!plant || plant.timeMs > timeMs || plant.x === undefined || plant.y === undefined) return;
  const defused = p.detail.events.find((e) => e.kind === "defuse" && e.timeMs <= timeMs);
  const [x, y] = v.at(plant.x, plant.y);

  ctx.globalAlpha = defused ? 0.4 : 0.6 + 0.4 * Math.sin(timeMs / 120);
  ctx.fillStyle = defused ? "#7ad4ff" : "#ff3b3b";
  ctx.beginPath();
  ctx.arc(x, y, 9 * dpr, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalAlpha = 1;
  ctx.fillStyle = "#0b0e14";
  ctx.font = `bold ${9 * dpr}px ui-sans-serif, system-ui`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("S", x, y);
}

function drawDeaths(v: View, p: Props) {
  const { ctx, dpr, timeMs } = v;
  for (const e of p.detail.events) {
    if (e.kind !== "kill" || e.x === undefined || e.y === undefined) continue;
    const age = timeMs - e.timeMs;
    if (age < 0 || age > DEATH_MARKER_MS) continue;
    const [x, y] = v.at(e.x, e.y);
    const victim = p.players.find((pl) => pl.pawn === e.target);
    ctx.globalAlpha = 0.85 * (1 - age / DEATH_MARKER_MS);
    ctx.strokeStyle = victim ? sideColor(victim, p.round) : "#ffffff";
    ctx.lineWidth = 2.5 * dpr;
    const r = 6 * dpr;
    ctx.beginPath();
    ctx.moveTo(x - r, y - r);
    ctx.lineTo(x + r, y + r);
    ctx.moveTo(x + r, y - r);
    ctx.lineTo(x - r, y + r);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
}

function drawPlayers(v: View, p: Props) {
  const { ctx, dpr, timeMs } = v;
  const t = p.tracks;
  if (!t) return;
  const xSign = Math.sign(p.map.xMultiplier) || 1;
  const ySign = Math.sign(p.map.yMultiplier) || -1;

  for (const player of p.players) {
    const index = t.pawns.indexOf(player.pawn);
    if (index < 0) continue;
    const s = sampleAt(t, index, timeMs);
    if (!s.present) continue;

    const [x, y] = v.at(s.x, s.y);
    const color = sideColor(player, p.round);
    const faded = v.dimmed(player.pawn);
    const alpha = faded ? 0.2 : 1;

    // Motion trail.
    const points = trail(t, index, timeMs, TRAIL_MS);
    if (points.length > 1) {
      ctx.globalAlpha = alpha * 0.35;
      ctx.strokeStyle = color;
      ctx.lineWidth = 2 * dpr;
      ctx.beginPath();
      points.forEach(([gx, gy], i) => {
        const [tx, ty] = v.at(gx, gy);
        if (i === 0) ctx.moveTo(tx, ty);
        else ctx.lineTo(tx, ty);
      });
      ctx.stroke();
    }

    // View cone, oriented through the same projection as the position.
    const yaw = (s.yaw * Math.PI) / 180;
    const angle = Math.atan2(Math.cos(yaw) * ySign, Math.sin(yaw) * xSign);
    const spread = 0.42;
    const reach = 34 * dpr;
    const cone = ctx.createRadialGradient(x, y, 0, x, y, reach);
    cone.addColorStop(0, hexAlpha(color, faded ? 0.12 : 0.35));
    cone.addColorStop(1, hexAlpha(color, 0));
    ctx.globalAlpha = 1;
    ctx.fillStyle = cone;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.arc(x, y, reach, angle - spread, angle + spread);
    ctx.closePath();
    ctx.fill();

    // The player marker: agent portrait when we have one, initial otherwise.
    const radius = (v.focusPawn === player.pawn ? 12 : 10) * dpr;
    ctx.globalAlpha = alpha;
    ctx.save();
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.closePath();
    ctx.fillStyle = "#0b0e14";
    ctx.fill();
    const agent = p.agents.get(player.codename);
    const portrait = image(agent?.icon);
    if (portrait) {
      ctx.clip();
      ctx.drawImage(portrait, x - radius, y - radius, radius * 2, radius * 2);
    } else {
      ctx.fillStyle = color;
      ctx.font = `bold ${radius}px ui-sans-serif, system-ui`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText((agent?.displayName ?? player.codename).slice(0, 1).toUpperCase(), x, y);
    }
    ctx.restore();

    ctx.globalAlpha = alpha;
    ctx.lineWidth = (v.focusPawn === player.pawn ? 3 : 2) * dpr;
    ctx.strokeStyle = color;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.stroke();

    drawPlayerEffects(v, p, player.pawn, x, y, radius, alpha);
  }
  ctx.globalAlpha = 1;
}

/**
 * What an ability is doing to this player, drawn as coloured arcs around the
 * marker: one segment per active state, so a blinded and suppressed player is
 * readable at a glance without covering the portrait.
 */
function drawPlayerEffects(
  v: View,
  p: Props,
  pawn: number,
  x: number,
  y: number,
  radius: number,
  alpha: number,
) {
  const { ctx, dpr, timeMs } = v;
  const active = effectsAt(p.detail.effects, pawn, timeMs, p.round.endMs).filter((e) =>
    MAP_EFFECTS.includes(e.category),
  );
  if (active.length === 0) return;

  const ring = radius + 4 * dpr;
  const gap = 0.18;
  const step = (Math.PI * 2) / active.length;
  ctx.lineWidth = 3 * dpr;
  active.forEach((effect, i) => {
    const style = EFFECT_STYLE[effect.category];
    ctx.globalAlpha = alpha;
    ctx.strokeStyle = style.color;
    ctx.beginPath();
    ctx.arc(x, y, ring, i * step + gap, (i + 1) * step - gap);
    ctx.stroke();
  });

  // A blind is the one state that should be impossible to miss.
  if (active.some((e) => e.category === "blind")) {
    const halo = ctx.createRadialGradient(x, y, radius, x, y, radius * 2.6);
    halo.addColorStop(0, hexAlpha(EFFECT_STYLE.blind.color, 0.55 * alpha));
    halo.addColorStop(1, hexAlpha(EFFECT_STYLE.blind.color, 0));
    ctx.fillStyle = halo;
    ctx.beginPath();
    ctx.arc(x, y, radius * 2.6, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

/** Adds an alpha channel to a #rrggbb colour. */
function hexAlpha(hex: string, alpha: number): string {
  const value = hex.replace("#", "");
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export type { Utility };
